import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
  DownloadIcon,
  UploadIcon,
  CheckIcon,
  CloseIcon,
  FilterIcon,
  HelpCircleIcon,
  GoogleIcon,
} from '../icons';
import { useCustomFields, useLabels, useLists } from '../../lib/crm';
import { useAccounts } from '../../lib/accounts';
import {
  tokenizeCsv,
  autoMapColumns,
  type StandardField,
} from '../../lib/csv';
import {
  importSubscribers,
  importGoogleSheet,
  subscriberImportTemplate,
} from '../../lib/subscribers';
import { numberLabel } from '../../lib/whatsappApi';
import {
  googleAccountsList,
  importGoogleContacts,
  type GoogleAccountDoc,
} from '../../lib/google';

interface ImportSubscribersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialTab?: ImportTab;
}

type ImportTab = 'csv' | 'sheet' | 'contacts';

interface ImportSummary {
  imported: number;
  skipped: number;
  duplicates: number;
  consentGranted?: number;
}

export default function ImportSubscribersModal({
  isOpen,
  onClose,
  onSuccess,
  initialTab = 'csv',
}: ImportSubscribersModalProps) {
  const { t } = useI18n();
  const accounts = useAccounts();
  const { labels } = useLabels();
  const { lists } = useLists();
  const { customFields } = useCustomFields();

  const [tab, setTab] = useState<ImportTab>(initialTab);
  const [file, setFile] = useState<File | null>(null);
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, StandardField>>({});
  const hasConsentColumn = Object.values(columnMapping).includes('marketing_consent');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [sheetUrl, setSheetUrl] = useState('');
  // Marketing consent evidence for rows whose marketing_consent column is "yes".
  const [consentNote, setConsentNote] = useState('');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Google Contacts State
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccountDoc[]>([]);
  const [selectedGoogleAccountId, setSelectedGoogleAccountId] = useState<string>('');
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('');
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [loadingGoogleAccounts, setLoadingGoogleAccounts] = useState(false);
  const [importingContacts, setImportingContacts] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && initialTab) {
      setTab(initialTab);
    }
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (isOpen && tab === 'contacts') {
      let active = true;
      setLoadingGoogleAccounts(true);
      googleAccountsList()
        .then((res) => {
          if (!active) return;
          const accs = res.accounts || [];
          setGoogleAccounts(accs);
          if (accs.length > 0 && !selectedGoogleAccountId) {
            setSelectedGoogleAccountId(accs[0].id);
          }
        })
        .catch((err) => {
          if (active) setError(err.message || 'Failed to load Google accounts');
        })
        .finally(() => {
          if (active) setLoadingGoogleAccounts(false);
        });
      return () => {
        active = false;
      };
    }
  }, [isOpen, tab]);

  // Custom field keys array for auto-mapping
  const cfKeys = customFields.map((cf) => cf.key);

  const resetState = () => {
    setFile(null);
    setParsedHeaders([]);
    setParsedRows([]);
    setColumnMapping({});
    setBusy(false);
    setError(null);
    setSummary(null);
    setSheetUrl('');
    setConsentNote('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Download sample CSV template from backend
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setError(null);
    try {
      const res = await subscriberImportTemplate({});
      const blob = new Blob([res.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'watag_subscribers_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback template if network/callable fails
      const fallbackHeaders = ['phone', 'name', 'email', 'country', 'language', 'labels', 'lists', ...cfKeys];
      const fallbackCsv = `${fallbackHeaders.join(',')}\n+96891234567,Ahmed Al-Busaidi,ahmed@example.com,OM,en,VIP,"Retail Customers"`;
      const blob = new Blob([fallbackCsv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'watag_subscribers_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Process selected file
  const handleFileChange = (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError(t('subs.import.invalidCsv') || 'Please select a valid .csv file.');
      return;
    }
    setError(null);
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = (e.target?.result as string) || '';
      const allRows = tokenizeCsv(content).filter((r) => r.some((c) => c.trim() !== ''));
      if (allRows.length === 0) {
        setError(t('subs.import.emptyFile') || 'The selected file is empty.');
        return;
      }
      const headers = allRows[0].map((h) => h.trim());
      const dataRows = allRows.slice(1);
      setParsedHeaders(headers);
      setParsedRows(dataRows);

      // Auto map columns
      const mapping = autoMapColumns(headers, cfKeys);
      setColumnMapping(mapping);
    };
    reader.readAsText(selectedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  // Perform CSV import
  const handleCsvImport = async () => {
    if (parsedRows.length === 0) return;

    // Validate that at least one column is mapped to 'phone'
    const phoneMapped = Object.values(columnMapping).some((val) => val === 'phone');
    if (!phoneMapped) {
      setError(t('subs.import.missingPhoneMapping') || 'You must map at least one column to Phone Number.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      // Transform parsed rows into backend payload objects
      const rowsPayload: Array<Record<string, any>> = parsedRows.map((row) => {
        const item: Record<string, any> = {};
        const customObj: Record<string, any> = {};

        parsedHeaders.forEach((header, idx) => {
          const mappedField = columnMapping[header] || 'skip';
          const val = (row[idx] ?? '').trim();
          if (!val || mappedField === 'skip') return;

          if (mappedField.startsWith('custom:')) {
            const key = mappedField.replace('custom:', '');
            customObj[key] = val;
          } else {
            item[mappedField] = val;
          }
        });

        // Flat keys: the callable only accepts scalar row values and treats
        // unknown columns as custom fields (a nested object failed validation).
        for (const [k, v] of Object.entries(customObj)) {
          if (!(k in item)) item[k] = v;
        }
        return item;
      });

      const note = consentNote.trim();
      const res = await importSubscribers({
        rows: rowsPayload,
        ...(hasConsentColumn && note.length >= 10 ? { marketingConsentNote: note } : {}),
      });
      setSummary({
        imported: res.imported,
        skipped: res.skipped,
        duplicates: res.duplicates,
        consentGranted: res.consentGranted ?? 0,
      });
      onSuccess?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Perform Google Sheet import
  const handleSheetImport = async () => {
    if (!sheetUrl.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await importGoogleSheet({ sheetUrl: sheetUrl.trim() });
      setSummary({
        imported: res.imported,
        skipped: res.skipped,
        duplicates: res.duplicates,
      });
      onSuccess?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t('subs.import.title') || 'Import Subscribers'}
    >
      <div className="space-y-5 max-w-2xl">
        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            type="button"
            onClick={() => { setTab('csv'); setError(null); }}
            className={`pb-3 px-4 text-xs font-semibold border-b-2 transition-colors ${
              tab === 'csv'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('subs.import.tabCsv') || 'CSV File'}
          </button>
          <button
            type="button"
            onClick={() => { setTab('sheet'); setError(null); }}
            className={`pb-3 px-4 text-xs font-semibold border-b-2 transition-colors ${
              tab === 'sheet'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('subs.import.tabGoogleSheet') || 'Google Sheet'}
          </button>
          <button
            type="button"
            onClick={() => { setTab('contacts'); setError(null); }}
            className={`pb-3 px-4 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${
              tab === 'contacts'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <span>{t('subs.import.tabGoogleContacts') || 'Google Contacts'}</span>
          </button>
        </div>

        {/* Error Notification */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center gap-2">
            <HelpCircleIcon size={16} className="shrink-0 text-red-500" />
            <span>{error}</span>
          </div>
        )}

        {/* SUMMARY CARD ON SUCCESS */}
        {summary ? (
          <div className="p-6 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
              <CheckIcon size={24} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                {t('subs.import.complete') || 'Import Completed'}
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                {t('subs.import.summaryDesc') || 'Your contacts have been processed and synced.'}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-lg font-bold text-emerald-600">{summary.imported}</div>
                <div className="text-[11px] text-slate-500 font-medium">
                  {t('subs.import.imported') || 'Imported'}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-lg font-bold text-amber-600">{summary.duplicates}</div>
                <div className="text-[11px] text-slate-500 font-medium">
                  {t('subs.import.duplicates') || 'Updated'}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="text-lg font-bold text-slate-600">{summary.skipped}</div>
                <div className="text-[11px] text-slate-500 font-medium">
                  {t('subs.import.skipped') || 'Skipped'}
                </div>
              </div>
            </div>

            {(summary.consentGranted ?? 0) > 0 && (
              <p className="text-xs font-semibold text-indigo-700">
                {t('subs.import.consentGranted', { count: summary.consentGranted ?? 0 })}
              </p>
            )}

            <div className="pt-4 flex justify-center gap-3">
              <Button variant="secondary" onClick={resetState}>
                {t('subs.import.importMore') || 'Import More'}
              </Button>
              <Button variant="primary" onClick={handleClose}>
                {t('common.done') || 'Done'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* TAB 1: CSV FILE */}
            {tab === 'csv' && (
              <div className="space-y-5">
                {/* Download Sample CSV Action */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                  <div className="flex items-center gap-2 text-slate-700">
                    <FilterIcon size={16} className="text-slate-500" />
                    <span>
                      {t('subs.import.templateHelp') ||
                        'Download our ready-to-use CSV template with your tenant custom fields.'}
                    </span>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    className="shrink-0 flex items-center gap-1.5"
                  >
                    <DownloadIcon size={14} />
                    <span>
                      {downloadingTemplate
                        ? t('common.downloading') || 'Downloading...'
                        : t('subs.import.downloadTemplate') || 'Download Sample CSV'}
                    </span>
                  </Button>
                </div>

                {/* File Dropzone */}
                {!file ? (
                  <div
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 hover:border-indigo-400 rounded-xl p-8 text-center cursor-pointer transition-colors bg-white hover:bg-slate-50/50"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileChange(e.target.files[0]);
                        }
                      }}
                    />
                    <UploadIcon size={32} className="mx-auto text-slate-400 mb-2" />
                    <p className="text-xs font-semibold text-slate-700">
                      {t('subs.import.dropPrompt') || 'Click to browse or drag and drop your .csv file here'}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {t('subs.import.dropHint') || 'Supports UTF-8 CSV with commas or semicolons'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Selected File Info */}
                    <div className="flex items-center justify-between p-3 bg-indigo-50/60 border border-indigo-100 rounded-lg text-xs">
                      <div className="flex items-center gap-2">
                        <UploadIcon size={18} className="text-indigo-600" />
                        <div>
                          <span className="font-semibold text-slate-900">{file.name}</span>
                          <span className="text-slate-500 ms-2">
                            ({parsedRows.length} {t('subs.import.rowsCount') || 'subscribers found'})
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={resetState}
                        aria-label={t('common.cancel')}
                        className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-white"
                      >
                        <CloseIcon size={14} />
                      </button>
                    </div>

                    {/* Column Mapping & Preview Table */}
                    <div>
                      <h4 className="text-xs font-bold text-slate-900 mb-2">
                        {t('subs.import.mapColumns') || 'Map Columns to Subscriber Fields'}
                      </h4>
                      <p className="text-[11px] text-slate-500 mb-3">
                        {t('subs.import.mapDesc') ||
                          'Match each column from your CSV to WaTag fields or custom attributes.'}
                      </p>

                      <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-64">
                        <table className="w-full text-start text-xs border-collapse">
                          <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
                            <tr>
                              <th className="py-2.5 px-3 text-start font-semibold text-slate-700 w-1/4">
                                {t('subs.import.colHeader') || 'CSV Column'}
                              </th>
                              <th className="py-2.5 px-3 text-start font-semibold text-slate-700 w-1/3">
                                {t('subs.import.mapTo') || 'Map to Field'}
                              </th>
                              <th className="py-2.5 px-3 text-start font-semibold text-slate-700">
                                {t('subs.import.sampleValue') || 'Sample Data (Row 1)'}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {parsedHeaders.map((header) => {
                              const sampleVal = parsedRows[0]?.[parsedHeaders.indexOf(header)] || '—';
                              const currentMapping = columnMapping[header] || 'skip';

                              return (
                                <tr key={header} className="hover:bg-slate-50/70">
                                  <td className="py-2 px-3 font-medium text-slate-800">
                                    {header}
                                  </td>
                                  <td className="py-2 px-3">
                                    <select
                                      value={currentMapping}
                                      onChange={(e) =>
                                        setColumnMapping({
                                          ...columnMapping,
                                          [header]: e.target.value as StandardField,
                                        })
                                      }
                                      className={`w-full rounded-md border text-xs py-1 px-2 outline-none ${
                                        currentMapping === 'phone'
                                          ? 'border-indigo-500 bg-indigo-50/30 font-semibold text-indigo-900'
                                          : currentMapping === 'skip'
                                          ? 'border-slate-200 text-slate-400 bg-slate-50'
                                          : 'border-slate-200 text-slate-700 bg-white'
                                      }`}
                                    >
                                      <optgroup label="Standard Fields">
                                        <option value="phone">
                                          ★ {t('subs.field.phone') || 'Phone (Required E.164)'}
                                        </option>
                                        <option value="name">{t('subs.field.name') || 'Full Name'}</option>
                                        <option value="email">{t('subs.field.email') || 'Email'}</option>
                                        <option value="country">{t('subs.field.country') || 'Country Code'}</option>
                                        <option value="language">{t('subs.field.language') || 'Language'}</option>
                                        <option value="lists">{t('subs.field.lists') || 'Lists (Comma-sep)'}</option>
                                        <option value="labels">{t('subs.field.labels') || 'Labels (Comma-sep)'}</option>
                                        <option value="status">{t('subs.field.status') || 'Status (Opt-in)'}</option>
                                        <option value="marketing_consent">{t('subs.field.marketingConsent')}</option>
                                      </optgroup>

                                      {customFields.length > 0 && (
                                        <optgroup label="Custom Fields">
                                          {customFields.map((cf) => (
                                            <option key={cf.id} value={`custom:${cf.key}`}>
                                              {cf.label} ({cf.key})
                                            </option>
                                          ))}
                                        </optgroup>
                                      )}

                                      <optgroup label="Actions">
                                        <option value="skip">
                                          {t('subs.import.skipColumn') || '— Skip / Do not import —'}
                                        </option>
                                      </optgroup>
                                    </select>
                                  </td>
                                  <td className="py-2 px-3 text-slate-500 truncate max-w-xs font-mono text-[11px]">
                                    {sampleVal}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Marketing consent evidence — only when a column is mapped to it */}
                    {hasConsentColumn && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 space-y-2 dark:border-amber-900 dark:bg-amber-950/30">
                        <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-200">
                          {t('subs.import.consentHelp')}
                        </p>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                          {t('subs.mconsent.label')}
                        </label>
                        <textarea
                          value={consentNote}
                          rows={2}
                          onChange={(e) => setConsentNote(e.target.value)}
                          placeholder={t('subs.mconsent.placeholder')}
                          className="w-full rounded-lg border border-slate-200 p-2.5 text-xs outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        {consentNote.trim().length < 10 && (
                          <p className="text-[11px] font-semibold text-amber-800 dark:text-amber-300">
                            {t('subs.import.consentNoteMissing')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="pt-2 flex items-center justify-end gap-3">
                      <Button variant="secondary" onClick={resetState} disabled={busy}>
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleCsvImport}
                        disabled={busy}
                        className="flex items-center gap-1.5"
                      >
                        {busy && (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        <span>
                          {t('subs.import.submitBtn') || `Import ${parsedRows.length} Subscribers`}
                        </span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: GOOGLE SHEET */}
            {tab === 'sheet' && (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
                  <p className="font-semibold mb-1">
                    {t('subs.import.sheetInstructionsTitle') || 'Google Sheet Requirements:'}
                  </p>
                  <ul className="list-disc ps-4 space-y-0.5 text-blue-700">
                    <li>{t('subs.import.sheetReq1') || 'The sheet must have a header row with column names.'}</li>
                    <li>
                      {t('subs.import.sheetReq2') ||
                        'Share the document with "Anyone with the link can view" or share with service account.'}
                    </li>
                    <li>{t('subs.import.sheetReq3') || 'Must include a phone number column.'}</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {t('subs.import.sheetUrlLabel') || 'Google Sheet Shareable URL'}
                  </label>
                  <input
                    type="url"
                    value={sheetUrl}
                    onChange={(e) => setSheetUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/edit"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500 font-mono"
                  />
                </div>

                <div className="pt-2 flex items-center justify-end gap-3">
                  <Button variant="secondary" onClick={handleClose} disabled={busy}>
                    {t('common.cancel') || 'Cancel'}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSheetImport}
                    disabled={busy || !sheetUrl.trim()}
                    className="flex items-center gap-1.5"
                  >
                    {busy && (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    <span>{t('subs.import.importSheetBtn') || 'Import from Sheet'}</span>
                  </Button>
                </div>
              </div>
            )}

            {/* TAB 3: GOOGLE CONTACTS */}
            {tab === 'contacts' && (
              <div className="space-y-4">
                {loadingGoogleAccounts ? (
                  <div className="py-10 flex flex-col items-center justify-center text-slate-500 gap-2">
                    <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">{t('common.loading') || 'Loading Google accounts...'}</span>
                  </div>
                ) : googleAccounts.length === 0 ? (
                  <div className="p-8 text-center space-y-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
                      <GoogleIcon size={24} />
                    </div>
                    <h4 className="text-sm font-bold text-slate-900">
                      {t('google.empty.title') || 'No Google Account Connected'}
                    </h4>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto">
                      {t('subs.import.connectGooglePrompt') ||
                        'Connect your Google Workspace or Gmail account to import contacts directly into your WhatsApp subscriber lists.'}
                    </p>
                    <div className="pt-2">
                      <Button
                        variant="primary"
                        onClick={() => {
                          window.location.href = '/google-integration';
                        }}
                        leadingIcon={<GoogleIcon size={16} />}
                      >
                        {t('google.accounts.connectAnother') || 'Connect Google Account'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
                      <p className="font-semibold mb-1">
                        {t('subs.import.contactsInfoTitle') || 'Google People API Contacts Import:'}
                      </p>
                      <p className="text-blue-700 text-[11px] leading-relaxed">
                        {t('subs.import.contactsInfoDesc') ||
                          'Fetches contacts with phone numbers, normalizes them to E.164 format, and imports or updates them in your subscribers list.'}
                      </p>
                    </div>

                    {/* Google Account Selector */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        {t('google.contacts.selectAccount') || 'Select Google Account'}
                      </label>
                      <select
                        value={selectedGoogleAccountId}
                        onChange={(e) => setSelectedGoogleAccountId(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500 font-medium"
                      >
                        {googleAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {acc.email} ({acc.status === 'ok' ? 'Active' : 'Re-auth Required'})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Target WhatsApp Number */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        {t('subs.import.targetBotNumber') || 'Associated WhatsApp Line (Optional)'}
                      </label>
                      <select
                        value={selectedIntegrationId}
                        onChange={(e) => setSelectedIntegrationId(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500 font-medium"
                      >
                        <option value="">{t('subs.import.allLines') || 'All / Default Number'}</option>
                        {accounts.numbers.map((acc) => (
                          <option key={acc.id} value={acc.id}>
                            {numberLabel(acc)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Optional Labels */}
                    {labels.length > 0 && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          {t('subs.import.assignLabels') || 'Assign Labels (Optional)'}
                        </label>
                        <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200 max-h-24 overflow-y-auto">
                          {labels.map((lbl) => {
                            const isSelected = selectedLabelIds.includes(lbl.id);
                            return (
                              <button
                                key={lbl.id}
                                type="button"
                                onClick={() => {
                                  setSelectedLabelIds((prev) =>
                                    isSelected ? prev.filter((id) => id !== lbl.id) : [...prev, lbl.id],
                                  );
                                }}
                                className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                                  isSelected
                                    ? 'bg-indigo-600 text-white border-indigo-600 font-bold'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                {lbl.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Optional Lists */}
                    {lists.length > 0 && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          {t('subs.import.addToLists') || 'Add to Lists (Optional)'}
                        </label>
                        <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200 max-h-24 overflow-y-auto">
                          {lists.map((lst) => {
                            const isSelected = selectedListIds.includes(lst.id);
                            return (
                              <button
                                key={lst.id}
                                type="button"
                                onClick={() => {
                                  setSelectedListIds((prev) =>
                                    isSelected ? prev.filter((id) => id !== lst.id) : [...prev, lst.id],
                                  );
                                }}
                                className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                                  isSelected
                                    ? 'bg-emerald-600 text-white border-emerald-600 font-bold'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                {lst.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="pt-2 flex items-center justify-end gap-3 border-t border-slate-100">
                      <Button variant="secondary" onClick={handleClose} disabled={importingContacts}>
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={async () => {
                          if (!selectedGoogleAccountId) {
                            setError(t('google.contacts.selectAccountErr') || 'Please select a Google account.');
                            return;
                          }
                          try {
                            setImportingContacts(true);
                            setError(null);
                            const res = await importGoogleContacts({
                              accountId: selectedGoogleAccountId,
                              integrationId: selectedIntegrationId || undefined,
                              labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
                              listIds: selectedListIds.length > 0 ? selectedListIds : undefined,
                            });
                            setSummary({
                              imported: res.imported,
                              skipped: res.skipped,
                              duplicates: 0,
                            });
                            onSuccess?.();
                          } catch (err: any) {
                            setError(err.message || 'Failed to import Google contacts.');
                          } finally {
                            setImportingContacts(false);
                          }
                        }}
                        disabled={importingContacts || !selectedGoogleAccountId}
                        className="flex items-center gap-1.5"
                      >
                        {importingContacts && (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        <span>{t('subs.import.importContactsBtn') || 'Import Google Contacts'}</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
